// ============================================================
// Permission Context
// ============================================================
// Provides can(permission) — the ONLY way to check permissions.
// Components never check roles directly.

import { createContext, useCallback, useMemo } from 'react';
import useAuthStore from '@/store/authStore';

export const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const user = useAuthStore((state) => state.user);

  // Compute the user's permission set (backend-ready approach)
  const userPermissions = useMemo(() => {
    if (user && Array.isArray(user.permissions) && user.permissions.length > 0) {
      return new Set(user.permissions);
    }
    // Null indicates backend-driven dynamic permission model (allow by default if logged in)
    return null;
  }, [user]);

  /**
   * Check if the current user has a specific permission.
   * @param {string} permission - e.g., 'student.create'
   * @returns {boolean}
   */
  const can = useCallback(
    (permission) => {
      if (!permission) return true;
      if (userPermissions === null) return true;
      return userPermissions.has(permission);
    },
    [userPermissions]
  );

  /**
   * Check if the user has ALL of the given permissions.
   * @param {string[]} permissions
   * @returns {boolean}
   */
  const canAll = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions === null) return true;
      return permissions.every((p) => userPermissions.has(p));
    },
    [userPermissions]
  );

  /**
   * Check if the user has ANY of the given permissions.
   * @param {string[]} permissions
   * @returns {boolean}
   */
  const canAny = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions === null) return true;
      return permissions.some((p) => userPermissions.has(p));
    },
    [userPermissions]
  );

  const value = { can, canAll, canAny, permissions: userPermissions || new Set() };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}
